import { createReducer, on } from '@ngrx/store';
import * as Actions from './actions';
import * as RootActions from '../../actions';
import { OnboardedVehicleDto, OrganizationAgrirouterAccountDto } from '@services/api/sdf-agrirouter-api.service';
import { Tractor } from '@models/tractor';

// Export actions
export * from './actions';

export interface State {
  accounts: OrganizationAgrirouterAccountDto[];
  selectedAccount: OrganizationAgrirouterAccountDto | null;
  onboardedVehicles: {
    data: OnboardedVehicleDto[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    }
    filters: {
      qSearch?: string;
    };
  };
  notOnboardedVehicles: {
    data: Tractor[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    }
  };
  selectedVin: string | null;
  sentFiles: {
    data: any[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    }
  };
  receivedFiles: {
    data: any[];
    totalCount: number;
    isLoading: boolean;
    paginator: {
      page: number;
      pageSize: number;
    }
  }
  loadingAccounts: boolean;
  error: any | null;
}

export const initialState: State = {
  accounts: [],
  selectedAccount: null,
  onboardedVehicles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    },
    filters: {
      qSearch: '',
    }
  },
  notOnboardedVehicles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    }
  },
  selectedVin: null,
  sentFiles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    }
  },
  receivedFiles: {
    data: [],
    totalCount: 0,
    isLoading: false,
    paginator: {
      page: 1,
      pageSize: 10,
    }
  },
  loadingAccounts: false,
  error: null,
};

// Selectors
export const selectConnectionsAccounts = (state) => state.connections.accounts;
export const selectConnectionsSelectedAccount = (state) => state.connections.selectedAccount;
export const selectConnectionsOnboardedVehicles = (state) => state.connections.onboardedVehicles;
export const selectConnectionsNotOnboardedVehicles = (state) => state.connections.notOnboardedVehicles;
export const selectConnectionsSentFiles = (state) => state.connections.sentFiles;
export const selectConnectionsReceivedFiles = (state) => state.connections.receivedFiles;
export const selectConnectionsSelectedVin = (state) => state.connections.selectedVin;
export const selectConnectionsLoadingAccounts = (state) => state.connections.loadingAccounts;
export const selectConnectionsQSearch = (state) => state.connections.qSearch;
export const selectConnectionsError = (state) => state.connections.error;

// Reducer
export const connectionsReducer = createReducer(
  initialState,
  on(RootActions.resetState, () => ({ ...initialState })),
  on(Actions.resetConnectionsState, () => ({ ...initialState })),

  // Load Accounts
  on(Actions.loadAccountsSuccess, (state, { accounts }) => ({
    ...state,
    accounts,
    selectedAccount: accounts.length > 0 ? accounts[0] : null,
    error: null,
  })),
  on(Actions.loadAccountsFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Select Account
  on(Actions.selectAccount, (state, { account }) => ({
    ...state,
    selectedAccount: account,
  })),

  // Remove Account
  on(Actions.removeAccountSuccess, (state, { accountId }) => {
    const updatedAccounts = state.accounts.filter((acc) => acc.agrirouterAccountId !== accountId);
    return {
      ...state,
      accounts: updatedAccounts,
      selectedAccount: updatedAccounts.length > 0 ? updatedAccounts[0] : null,
    };
  }),
  on(Actions.removeAccountFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(Actions.setSelectedVin, (state, { selectedVin }) => ({
    ...state,
    selectedVin,
  })),
  on(Actions.setOnboardedVehiclesLoading, (state, { isLoading }) => ({
    ...state,
    onboardedVehicles: {
      ...state.onboardedVehicles,
      isLoading,
    }
  })),
  on(Actions.setNotOnboardedVehiclesLoading, (state, { isLoading }) => ({
    ...state,
    notOnboardedVehicles: {
      ...state.notOnboardedVehicles,
      isLoading,
    }
  })),
  on(Actions.setSentFilesLoading, (state, { isLoading }) => ({
    ...state,
    sentFiles: {
      ...state.sentFiles,
      isLoading,
    }
  })),
  on(Actions.setReceivedFilesLoading, (state, { isLoading }) => ({
    ...state,
    receivedFiles: {
      ...state.receivedFiles,
      isLoading,
    }
  })),
  on(Actions.setOnboardedVehiclesFilters, (state, { filters }) => ({
    ...state,
    onboardedVehicles: {
      ...state.onboardedVehicles,
      filters,
    }
  })),
  on(Actions.setOnboardedVehiclesPaginator, (state, { paginator }) => ({
    ...state,
    onboardedVehicles: {
      ...state.onboardedVehicles,
      paginator,
    }
  })),
  on(Actions.setNotOnboardedVehiclesPaginator, (state, { paginator }) => ({
    ...state,
    notOnboardedVehicles: {
      ...state.notOnboardedVehicles,
      paginator,
    }
  })),
  on(Actions.setSentFilesPaginator, (state, { paginator }) => ({
    ...state,
    sentFiles: {
      ...state.sentFiles,
      paginator,
    }
  })),
  on(Actions.setReceivedFilesPaginator, (state, { paginator }) => ({
    ...state,
    receivedFiles: {
      ...state.receivedFiles,
      paginator,
    }
  })),
  on(Actions.setOnboardedVehiclesList, (state, { response }) => ({
    ...state,
    onboardedVehicles: {
      ...state.onboardedVehicles,
      data: response.data,
      totalCount: response.totalCount,
    }
  })),
  on(Actions.setNotOnboardedVehiclesList, (state, { response }) => ({
    ...state,
    notOnboardedVehicles: {
      ...state.notOnboardedVehicles,
      data: response.data,
      totalCount: response.totalCount,
    }
  })),
  on(Actions.setSentFilesList, (state, { response }) => ({
    ...state,
    sentFiles: {
      ...state.sentFiles,
      data: response.data,
      totalCount: response.totalCount,
    }
  })),
  on(Actions.setReceivedFilesList, (state, { response }) => ({
    ...state,
    receivedFiles: {
      ...state.receivedFiles,
      data: response.data,
      totalCount: response.totalCount,
    }
  })),

  // Delete File
  on(Actions.deleteFileSuccess, (state, { fileId, direction }) => {
    const fileList = direction === 'sent' ? state.sentFiles.data : state.receivedFiles.data;
    const updatedFiles = fileList.filter((f) => f.bucketFilename !== fileId && f.bucketFileId !== fileId);

    return {
      ...state,
      sentFiles: {
        ...state.sentFiles,
        data: direction === 'sent' ? updatedFiles : state.sentFiles.data,
      },
      receivedFiles: {
        ...state.receivedFiles,
        data: direction === 'received' ? updatedFiles : state.receivedFiles.data,
      }
    };
  }),
  on(Actions.deleteFileFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Loading States
  // on(Actions.setLoadingAccounts, (state, { value }) => ({
  //   ...state,
  //   loadingAccounts: value,
  // })),
  // on(Actions.setLoadingOnboardedVehicles, (state, { value }) => ({
  //   ...state,
  //   loadingOnboardedVehicles: value,
  // })),
  // on(Actions.setLoadingNotOnboardedVehicles, (state, { value }) => ({
  //   ...state,
  //   loadingNotOnboardedVehicles: value,
  // })),
  // on(Actions.setLoadingSentFiles, (state, { value }) => ({
  //   ...state,
  //   loadingSentFiles: value,
  // })),
  // on(Actions.setLoadingReceivedFiles, (state, { value }) => ({
  //   ...state,
  //   loadingReceivedFiles: value,
  // })),
  // on(Actions.setOnboardedPaginator, (state, { page, pageSize }) => ({
  //   ...state,
  //   onboardedPaginator: {
  //     ...state.onboardedPaginator,
  //     page,
  //     pageSize,
  //   },
  // })),
  // on(Actions.setNotOnboardedPaginator, (state, { page, pageSize }) => ({
  //   ...state,
  //   notOnboardedPaginator: {
  //     ...state.notOnboardedPaginator,
  //     page,
  //     pageSize,
  //   }
  // })),
  // on(Actions.setReceivedPaginator, (state, { page, pageSize }) => ({
  //   ...state,
  //   receivedPaginator: {
  //     ...state.receivedPaginator,
  //     page,
  //     pageSize,
  //   }
  // })),
  // on(Actions.setSentPaginator, (state, { page, pageSize }) => ({
  //   ...state,
  //   sentPaginator: {
  //     ...state.sentPaginator,
  //     page,
  //     pageSize,
  //   }
  // })),
  // on(Actions.setQSearch, (state, { q }) => ({
  //   ...state,
  //   qSearch: q,
  // })),

  // Reset Files
  on(Actions.resetFilesState, (state) => ({
    ...state,
    loadingSentFiles: false,
    loadingReceivedFiles: false,
  })),
);

